import React from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/auth/AuthProvider';
import { studyChatApi, getInitialDisplayName, shouldPromptForDisplayName } from './api';
import { useStudyChat } from './useStudyChat';
import type { StudyGroup, Message } from './types';
import { openChatPopout } from '@/modules/study-chat/openPopout';
import { useLocation, useNavigate } from 'react-router-dom';
import { SITE_ID, SITE_SLUG } from '@/constants/chat';
import { getCurrentSiteIdFromJwt, setActiveSite, ensureActiveSiteFromSlug } from '@/lib/site';

// ⬇️ shadcn dialog (accessible; avoids Radix warning)
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

// ⬇️ shadcn alert dialog for sign-in gating
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import * as RDX from '@radix-ui/react-dialog';
import { Pencil } from 'lucide-react';

function initials(name?: string) {
  const n = (name ?? '').trim();
  if (!n) return '🙂';
  const parts = n.split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() ?? '').join('') || '🙂';
}

function sameAuthor(a?: Message, b?: Message) {
  if (!a || !b) return false;
  return a.display_name === b.display_name && a.user_id === b.user_id;
}

export default function StudyChatBody({
  subjectId,
  maxGroups = 5,
  onOpenChat,
  subjectName,
}: {
  subjectId?: number;
  maxGroups?: number;
  onOpenChat?: (group: StudyGroup) => void;
  subjectName?: string;
}) {
  const api = useMemo(() => studyChatApi(), []);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<StudyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState<StudyGroup | null>(null);

  // new-group dialog state
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // NEW: creator display name field in "New Group"
  const [newCreatorName, setNewCreatorName] = useState(''); // NEW

  // NEW: sign-in prompt state
  const [signinPromptOpen, setSigninPromptOpen] = useState(false);

  const { search } = useLocation();
  const titleFromQuery = useMemo(() => {
    const p = new URLSearchParams(search);
    const t = p.get('title');
    return t ? t.trim() : '';
  }, [search]);
  const subjectDisplay = (subjectName && subjectName.trim()) || titleFromQuery || '';

  const apiLocal = useMemo(() => studyChatApi(), []);
  const [displayName, setDisplayName] = useState<string>('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // 1) If signed in, make sure JWT carries the correct site for RLS
        if (user) {
          let desired = SITE_ID ?? null;

          // If no SITE_ID but we have a slug, resolve once and cache
          if (!desired && typeof SITE_SLUG === 'string' && SITE_SLUG) {
            desired = await ensureActiveSiteFromSlug(SITE_SLUG);
          }

          // Only update if JWT is missing or mismatched (avoid token churn)
          if (desired) {
            const current = await getCurrentSiteIdFromJwt();
            if (current !== desired) {
              await setActiveSite(desired);
            }
          }
        }

        // 2) Now safe to load site-scoped data
        const list = await api.listGroups(subjectId);
        setGroups(list);
        if (list[0]) setActiveGroup(list[0]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, user]);

  // Suggest “<Subject> Group N”
  const suggestedTitle = useMemo(() => {
    const countForSubject = groups.filter(g =>
      subjectId === undefined ? true : g.subject_id === subjectId
    ).length;
  
    return countForSubject === 0 ? 'General' : `Group ${countForSubject + 1}`;
  }, [groups, subjectId]);

  useEffect(() => setNewTitle(suggestedTitle), [suggestedTitle]);

  // Prefill creator display name when dialog opens or auth changes
  useEffect(() => {
    if (!newOpen) return;
    setNewCreatorName(prev => prev || getInitialDisplayName(user));
  }, [newOpen]); // NEW

  // Let joinWithApi accept an optional explicit displayName (clean path; no user_metadata dep)
  const joinWithApi = useCallback(
    async (groupId: number, displayName?: string) => {
      const name = (displayName ?? getInitialDisplayName(user)).slice(0, 30) || 'Member';
      // API now upserts membership (no duplicate errors)
      await api.joinGroup(groupId, name);
    },
    [api, user]
  );

  const canCreate =
    !!user &&
    subjectId !== undefined &&
    groups.filter((g) => g.subject_id === subjectId).length < (maxGroups ?? 5);

  async function handleCreateGroup() {
    try {
      setCreating(true);
      setCreateError('');

      const finalTitle = (newTitle || suggestedTitle).trim();
      const creatorName = (newCreatorName ?? '').trim().slice(0, 30); // NEW

      const g = await api.createGroup({
        subject_id: subjectId,
        title: finalTitle,
        is_default: false,
        description: newDesc || undefined,
        creator_display_name: creatorName || undefined, // NEW
      });

      // Creator is auto-added as owner by API; no extra join needed
      setGroups(prev => [...prev, g]);
      setActiveGroup(g);
      setNewOpen(false);
      setNewDesc('');
    } catch (err: any) {
      const msg = (err?.message || String(err));
      if (msg.includes('You already created a group for this subject')) {
        setCreateError('A group for this subject already exists under your account.');
      } else if (msg.toLowerCase().includes('duplicate')) {
        setCreateError('That name already exists. Try a different name.');
      } else {
        setCreateError('Could not create group. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  }

  const handlePopout = useCallback(() => {
    if (!activeGroup) return;
    openChatPopout({
      groupId: activeGroup.id,
      subjectId,
      subjectTitle: subjectDisplay || (subjectId ? `Subject #${subjectId}` : '')
    });
  }, [activeGroup]);

  return (
    <div className="chat-grid chat-body min-h-[calc(100dvh-var(--nav-h))]">
      {/* Sidebar */}
      <aside className="glass md:col-span-1 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-medium text-white/90 text-sm">Groups</h4>
          <Button
            size="sm"
            className="h-8 px-3 text-[12px]"
            variant="outline"
            onClick={() => {
              // gate on auth — show sign-in prompt if not logged in
              if (!user) {
                setSigninPromptOpen(true);
                return;
              }
              setNewOpen(true);
            }}
          >
            + New group
          </Button>
        </div>

        <Input
          placeholder="Search groups"
          className="bg-white/5 border-white/10 chat-text h-8 text-[13px] mb-2"
          onChange={() => {/* hook up later if needed */}}
        />

        <div className="space-y-1.5 max-h-72 overflow-auto">
          {loading && <div className="text-xs text-white/60">Loading…</div>}
          {!loading && groups.length === 0 && (
            <div className="text-xs text-white/60">No groups yet.</div>
          )}

          {groups.map((g) => {
            const selected = activeGroup?.id === g.id;
            return (
              <button
                key={g.id}
                title={`Created ${new Date(g.created_at).toLocaleDateString()}`}
                onClick={() => { setActiveGroup(g); }}
                className={[
                  // NOTE: remove h-11 so the row can grow; align items to start for multi-line
                  "w-full text-left px-3 py-2 rounded-md transition flex items-start gap-2",
                  selected ? "ring-1 ring-cyan-400/30 bg-white/5" : "hover:bg-white/5"
                ].join(" ")}
              >
                <span className="avatar w-7 h-7 text-[10px] mt-0.5">SG</span>

                {/* The title container grows and wraps */}
                <span className="flex-1 text-[14px] text-white/90 whitespace-normal break-words line-clamp-2">
                  {g.title}
                </span>
              </button>

            );
          })}
        </div>
      </aside>

      {/* Chat panel */}
      <section className="glass md:col-span-1 p-0 overflow-hidden chat-panel min-h-0">
        {activeGroup ? (
          <JoinedChatArea
            currentGroup={activeGroup}
            subjectName={subjectDisplay || (subjectId ? `Subject #${subjectId}` : 'Subject')}
            onLaunch={onOpenChat ? (() => onOpenChat(activeGroup)) : handlePopout}
            joinWithApi={joinWithApi}
          />
        ) : (
          <div className="p-6 text-white/70 text-sm">Select a group to preview chat.</div>
        )}
      </section>

      {/* New Group Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create a new group</DialogTitle>
            <DialogDescription>
              Give your group a clear name. Others will see it in the list.
            </DialogDescription>
          </DialogHeader>

        <div className="space-y-3">
            <div>
              <label className="block text-xs text-white/70 mb-1">Group name</label>
              <Input
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="bg-white/5 border-white/10 h-9 text-[14px]"
                placeholder={suggestedTitle}
              />
              {createError && (
                <p className="mt-1 text-[11px] text-red-300/90">{createError}</p>
              )}
            </div>

            {/* NEW: Creator display name */}
            <div>
              <label className="block text-xs text-white/70 mb-1">Your display name</label>
              <Input
                value={newCreatorName}
                onChange={(e) => setNewCreatorName(e.target.value)}
                className="bg-white/5 border-white/10 h-9 text-[14px]"
                placeholder="e.g., Alex Q."
                maxLength={30}
              />
              <p className="mt-1 text-[11px] text-white/50">This appears next to your messages.</p>
            </div>

            <div>
              <label className="block text-xs text-white/70 mb-1">Description (optional)</label>
              <Input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                className="bg-white/5 border-white/10 h-9 text-[14px]"
                placeholder="What is this group for?"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroup}
              disabled={!canCreate || creating || !newTitle.trim()}
            >
              {creating ? 'Creating…' : 'Create group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign-in prompt */}
      <AlertDialog open={signinPromptOpen} onOpenChange={setSigninPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign in required</AlertDialogTitle>
            <AlertDialogDescription>
              You need to be signed in to create a new study group.
              Continue to the sign-in page?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate('/?go=signin')}>
              Sign in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function JoinedChatArea({
  currentGroup,
  subjectName,
  onLaunch,
  joinWithApi,
}: {
  currentGroup: StudyGroup;
  subjectName: string;
  onLaunch: () => void;
  joinWithApi: (groupId: number, displayName?: string) => Promise<void>;
}) {
  const { messages, loading, post, clear } = useStudyChat(Number(currentGroup.id));
  const [joined, setJoined] = useState(false);
  const [checking, setChecking] = useState(true); // NEW
  const [input, setInput] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null); // NEW
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const meId = (user as any)?.id ?? (user as any)?.user?.id;

  const isOwner = currentGroup?.created_by && meId &&
                  String(currentGroup.created_by) === String(meId);

  useEffect(() => {
    if (isOwner) setJoined(true);
  }, [isOwner]);

  // ✅ Single membership+name probe on mount / navigation
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChecking(true);

      if (!user) { setJoined(false); setChecking(false); setDisplayName(''); return; }

      try {
        const api = studyChatApi();
        const dn = await api.getMyDisplayName(currentGroup.id); // '' if not a member
        if (cancelled) return;

        if (dn && dn.trim().length > 0) {
          setJoined(true);
          setDisplayName(dn);
          if (meId) {
            const cacheKey = `sg_joined_${currentGroup.id}_${meId}`;
            localStorage.setItem(cacheKey, '1');
          }
        } else {
          setJoined(false);
          setDisplayName('');
        }
      } catch {
        if (!cancelled) {
          setJoined(false);
          setDisplayName('');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, meId, currentGroup.id]);

  // Scroll inside the ScrollArea viewport (not the page)
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    ) as HTMLDivElement | null;
    if (viewport) {
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
    }
  }, [messages.length]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'; // cap growth to 120px
  }, [input]);

  // Display-name dialog (local to join flow)
  const [nameOpen, setNameOpen] = useState(false);
  const [nameInput, setNameInput] = useState(getInitialDisplayName(user));
  const [displayName, setDisplayName] = useState<string>('');
  const apiLocal = useMemo(() => studyChatApi(), []);
  
  useEffect(() => {
    setNameInput(getInitialDisplayName(user));
  }, [user]);
  
  async function handleJoin() {
    if (!user || checking || joined) return; // NEW guard
    // Always prompt for a name first
    setNameInput(getInitialDisplayName(user));
    setNameOpen(true);
  }
  
  async function saveDisplayNameAndJoin() {
    if (joined) { setNameOpen(false); return; } // NEW guard
    const clean = (nameInput ?? '').trim().slice(0, 30);
    if (!clean) return;
    setNameOpen(false);
    try {
      await joinWithApi(currentGroup.id, clean);
    } catch (e) {
      console.error('joinWithApi failed:', e);
      return;
    }
    setJoined(true);
    if (meId) {
      const cacheKey = `sg_joined_${currentGroup.id}_${meId}`;
      localStorage.setItem(cacheKey, '1');
    }
    setDisplayName(clean);
  }

  async function send() {
    const body = input.trim();
    if (!body || !joined) return;
    setInput('');
    await post(body);
  }

  const subjectLabel =
    (subjectName && subjectName.trim()) ||
    (currentGroup?.subject_id != null ? `Subject #${currentGroup.subject_id}` : 'Subject');

  const signedIn = !!user;
  const pillLabel = !signedIn
    ? "Sign in to set name"
    : joined
      ? (displayName || "Member")
      : "Join to set name";

  const pillTitle = !signedIn
    ? "Sign in to set your display name"
    : !joined
      ? "Join the group to set your name"
      : "Edit display name";

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="sticky top-0 z-10 px-4 py-2 chat-chrome flex items-center justify-between backdrop-blur supports-[backdrop-filter]:bg-black/30">
        <div className="min-w-0">
          <div className="font-medium text-white/90 text-sm min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="truncate max-w-[30vw] md:max-w-[22rem]"
                title={currentGroup.title}
              >
                {currentGroup.title}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {/* quick edit of display name anytime (pill with overlaid pencil) */}
          <button
            type="button"
            onClick={() => {
              if (!joined) return;
              setNameInput(displayName || '');
              setNameOpen(true);
            }}
            aria-disabled={!joined}
            title={pillTitle}
            className={[
              "relative inline-flex items-center gap-2 px-3 h-8 rounded-md border text-[12px] transition",
              "border-white/10 bg-white/5 hover:bg-white/10",
              joined ? "text-white/90" : "opacity-50 cursor-not-allowed pointer-events-none"
            ].join(" ")}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-[10px]">
              {initials(signedIn ? (displayName || 'Member') : "Sign in")}
            </span>
            <span className="truncate max-w-[12rem]">
              {pillLabel}
            </span>
            <span className="absolute -right-1 -bottom-1 inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/20 bg-black/60">
              <Pencil size={10} />
            </span>
          </button>
          <Button
            variant="outline"
            title="Clears messages for everyone"
            onClick={async () => { await clear(); }}
            className="h-8 px-3 text-[12px] text-red-300 border-red-400/40 hover:bg-red-500/10"
          >
            Clear
          </Button>
          <Button onClick={onLaunch} className="h-8 px-3 text-[12px]">Pop Out</Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 min-h-0 messages overscroll-contain">
        <div className="space-y-1.5" aria-live="polite">
          {loading && <div className="text-xs text-white/60 px-4 pt-2">Loading…</div>}
          {!loading && messages.length === 0 && (
            <div className="text-xs text-white/60 px-4 pt-2">No messages yet.</div>
          )}

          {messages.map((m, i) => {
            const prev = i > 0 ? messages[i - 1] : undefined;
            const grouped = sameAuthor(m, prev);
            const isMe = m.user_id && meId && String(m.user_id) === String(meId);

            return (
              <div key={m.id} className={['msg-row', isMe ? 'msg-me' : ''].join(' ')}>
                {/* Header row only for the first in a sequence */}
                {!grouped && (
                  <div className="col-span-2 flex items-center gap-2 px-4 pt-2">
                    <div className="avatar select-none">{initials(m.display_name)}</div>
                    <span className="msg-name text-[13px] leading-4">{m.display_name}</span>
                    <span className="msg-time text-[11px] opacity-60">
                      {new Date(m.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                )}

                {/* Message bubble column (no separate header here anymore) */}
                <div className={['msg-group group', isMe ? 'col-start-1' : 'col-start-2'].join(' ')}>
                  <div className="bubble text-[13px] leading-5 px-3 py-1.5 max-w-[72ch]">{m.body}</div>
                  {grouped && (
                    <div className="msg-meta text-[11px] opacity-50 group-hover:opacity-90">
                      {new Date(m.created_at).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {/* Sticky join bar (shown until user joins) */}
      {!joined && (
        <div className="px-3 py-2 bg-white/5 border-t border-white/10 flex items-center justify-between text-[13px]">
          <span className="text-white/80">
            {checking ? 'Checking membership…' : 'You’re previewing this group. Join to send messages.'}
          </span>
          <Button className="h-8 px-3 text-[12px]" onClick={handleJoin} disabled={!user || checking}>
            Join group
          </Button>
        </div>
      )}

      {/* Composer */}
      <div className="p-2 chat-input">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={!user ? 'Sign in to chat…' : !joined ? 'Join to chat…' : 'Type a message…'}
            disabled={!user || !joined}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            className="w-full resize-none bg-white/5 border border-white/10 rounded-md px-3 py-2 text-[14px] leading-5 outline-none focus:ring-2 focus:ring-cyan-400/40"
          />
          <Button
            className="shrink-0 h-10 px-4 text-[13px]"
            onClick={send}
            disabled={!user || !joined || !input.trim()}
          >
            Send
          </Button>
        </div>
      </div>

      {/* Display Name Dialog (Join flow) */}
      <Dialog open={nameOpen} onOpenChange={setNameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choose your display name</DialogTitle>
            <DialogDescription>
              This name appears next to your messages in study groups.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="block text-xs text-white/70 mb-1">Display name</label>
            <Input
              autoFocus
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="bg-white/5 border-white/10 h-9 text-[14px]"
              placeholder="e.g., Alex Q."
              maxLength={30}
            />
            <p className="text-[11px] text-white/50">2–30 characters. You can change this later.</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNameOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const clean = (nameInput ?? '').trim().slice(0, 30);
                if (!clean) return;
                try {
                  await joinWithApi(currentGroup.id, clean);
                } catch (e) {
                  console.error('joinWithApi failed:', e);
                  return;
                }
                setDisplayName(clean);
                setJoined(true);
                if (meId) {
                  const cacheKey = `sg_joined_${currentGroup.id}_${meId}`;
                  localStorage.setItem(cacheKey, '1');
                }
                setNameOpen(false);
              }}
              disabled={!nameInput.trim() || nameInput.trim().length < 2}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
